// One-line confirmation toasts ("Saved to Plans in WaageWu"): showToast()
// from anywhere, and <ToastHost/> at the app root shows the current one.
// A new toast replaces the old; each goes away after DURATION_MS, or
// ACTION_DURATION_MS when it carries an action button ("Undo"). The
// mobile twin is mobile/lib/toast.ts (message only).

import { useSyncExternalStore } from 'react';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: number;
  message: string;
  action?: ToastAction;
}

const DURATION_MS = 3000;
const ACTION_DURATION_MS = 7000;

let current: Toast | null = null;
let seq = 0;
let timer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

export function showToast(message: string, opts?: { action?: ToastAction }): void {
  seq += 1;
  current = opts?.action ? { id: seq, message, action: opts.action } : { id: seq, message };
  if (timer) clearTimeout(timer);
  timer = setTimeout(dismissToast, opts?.action ? ACTION_DURATION_MS : DURATION_MS);
  emit();
}

export function dismissToast(): void {
  if (timer) clearTimeout(timer);
  timer = null;
  current = null;
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useToast(): Toast | null {
  return useSyncExternalStore(subscribe, () => current);
}
